/**
 * The gym sheets (`D202`): `edit=gym-new` saves the equipment in effect as a
 * named gym, `edit=gym:<id>` edits one, deletes it, or switches to it.
 *
 * ── ONLINE, EXCEPT THE SWITCH ──────────────────────────────────────────────
 * Creating, editing and deleting a preset are ordinary mutations whose
 * refusal the sheet shows: they happen on a sofa, not under a squat rack.
 * "Usar esta" is the one gym write the outbox carries (`gym.use`), the same
 * entry the readiness chip queues, so it is drawn at the tap and lands when
 * the network does.
 *
 * ── THE CREATE SENDS THE LIST IT SHOWS ─────────────────────────────────────
 * The server can copy the equipment in effect by itself, but the device's
 * copy is the one the person is looking at — an equipment save still in the
 * outbox included — so the list is sent explicitly. The id is minted once
 * per opening: a double tap on "Salvar" is the same gym (`D163`).
 */
import { catalogFiltersOptions } from "@repo/client/data/catalog";
import {
  createGymMutation,
  deleteGymMutation,
  equipmentOptions,
  gymsOptions,
  updateGymMutation,
} from "@repo/client/data/profile";
import type { EquipmentPlace, GymOutput } from "@repo/contract/training";
import {
  "gyms.create_helper" as createHelper,
  "gyms.create_title" as createTitle,
  "gyms.delete_body" as deleteBody,
  "gyms.delete_confirm" as deleteConfirm,
  "gyms.deleted" as deletedToast,
  "gyms.delete" as deleteLabel,
  "gyms.delete_title" as deleteTitle,
  "gyms.edit_title" as editTitle,
  "profile-editors.equipment_empty" as equipmentEmpty,
  "gyms.eyebrow" as eyebrowText,
  "gyms.name_error" as nameError,
  "gyms.name_helper" as nameHelper,
  "gyms.name_label" as nameLabel,
  "gyms.place_label" as placeLabel,
  "gyms.saved" as savedToast,
  "gyms.switched" as switchedToast,
  "gyms.use" as switchLabel,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { useCallback, useState } from "react";
import {
  gymUse,
  newClientId,
  useOutboxWrite,
} from "../../../data/account-writes";
import { useApi } from "../../../data/api";
import { useDataMutation, useDataQuery } from "../../../data/query-client";
import { Button, TextField } from "../../../ui/controls";
import { AlertDialog } from "../../../ui/overlay";
import { ChoiceForm } from "../editors/choice-form";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "../editors/editor-shell";
import { EquipmentForm } from "../editors/equipment-form";
import { placeOptions } from "../editors/options";
import { Helper } from "../editors/parts";
import { MAX_NAME, validGymName } from "./model";

const catalogueLocale = () => (getLocale().startsWith("pt") ? "pt" : "en");

function NameField(props: {
  error: boolean;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <TextField
      autoCapitalize="words"
      enterKeyHint="done"
      error={props.error ? nameError() : undefined}
      label={nameLabel()}
      maxLength={MAX_NAME}
      onValueChange={props.onChange}
      value={props.value}
    />
  );
}

/** `edit=gym-new`: the checklist in effect, saved under a name. */
export function GymCreateEditor({ host }: { readonly host: EditorHost }) {
  const api = useApi();
  const read = useSectionRead(equipmentOptions(api));
  const flow = useSaveFlow(useDataMutation(createGymMutation(api)), host);
  const [id] = useState(newClientId);
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const [place, setPlace] = useState<EquipmentPlace | null>(null);
  const shownPlace = place ?? read.data?.place ?? null;
  const available = read.data?.available ?? [];
  const typeName = useCallback((value: string) => {
    setTouched(true);
    setName(value);
  }, []);
  const save = useCallback(() => {
    setTouched(true);
    if (!validGymName(name) || available.length === 0) {
      return;
    }
    flow.save(
      {
        equipmentIds: [...available],
        id,
        name: name.trim(),
        place: shownPlace,
      },
      () => ({ message: savedToast() })
    );
  }, [available, flow, id, name, shownPlace]);
  return (
    <EditorShell
      disabled={!validGymName(name) || available.length === 0}
      eyebrow={eyebrowText()}
      failure={
        flow.failure ?? (available.length === 0 ? equipmentEmpty() : null)
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={save}
      pending={flow.pending}
      title={createTitle()}
    >
      <Helper>{createHelper()}</Helper>
      <Helper>{nameHelper()}</Helper>
      <NameField
        error={touched && !validGymName(name)}
        onChange={typeName}
        value={name}
      />
      <div className="mt-5">
        <ChoiceForm
          label={placeLabel()}
          onChange={setPlace}
          options={placeOptions()}
          value={shownPlace}
        />
      </div>
    </EditorShell>
  );
}

/** `edit=gym:<id>`: one gym — its name, place and list; delete; switch. */
export function GymEditor({
  host,
  gymId,
}: {
  readonly gymId: string;
  readonly host: EditorHost;
}) {
  const api = useApi();
  const list = useSectionRead(gymsOptions(api));
  const gym = list.data?.gyms.find((one) => one.id === gymId) ?? null;
  if (list.loading || list.loadFailed || gym === null) {
    return (
      <EditorShell
        eyebrow={eyebrowText()}
        host={host}
        loadFailed={list.loadFailed || (!list.loading && gym === null)}
        loading={list.loading}
        title={editTitle()}
      >
        {null}
      </EditorShell>
    );
  }
  return <LoadedGymEditor gym={gym} host={host} />;
}

function LoadedGymEditor({
  gym,
  host,
}: {
  readonly gym: GymOutput;
  readonly host: EditorHost;
}) {
  const api = useApi();
  const filters = useDataQuery(catalogFiltersOptions(api, catalogueLocale()));
  const flow = useSaveFlow(useDataMutation(updateGymMutation(api)), host);
  const removal = useDataMutation(deleteGymMutation(api));
  const write = useOutboxWrite();
  const [name, setName] = useState(gym.name);
  const [place, setPlace] = useState<EquipmentPlace | null>(gym.place);
  const [equipmentIds, setEquipmentIds] = useState<number[]>([
    ...gym.equipmentIds,
  ]);
  const [confirming, setConfirming] = useState(false);
  const { describeError, onClose, onSaved } = host;
  const valid = validGymName(name) && equipmentIds.length > 0;

  const save = useCallback(() => {
    if (!valid) {
      return;
    }
    flow.save(
      { equipmentIds, gymId: gym.id, name: name.trim(), place },
      () => ({ message: savedToast() })
    );
  }, [equipmentIds, flow, gym.id, name, place, valid]);

  const switchTo = useCallback(() => {
    write(
      gymUse({ equipmentIds: gym.equipmentIds, id: gym.id, place: gym.place })
    ).then(
      () => {
        onSaved({ message: switchedToast({ name: gym.name }) });
        onClose();
      },
      (error: unknown) => flow.setFailure(describeError(error))
    );
  }, [describeError, flow, gym, onClose, onSaved, write]);

  const askDelete = useCallback(() => setConfirming(true), []);
  const confirmOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setConfirming(false);
    }
  }, []);
  const { mutate: remove } = removal;
  const confirmDelete = useCallback(() => {
    remove(gym.id, {
      onError: (error) => {
        setConfirming(false);
        flow.setFailure(describeError(error));
      },
      onSuccess: () => {
        setConfirming(false);
        onSaved({ message: deletedToast() });
        onClose();
      },
    });
  }, [describeError, flow, gym.id, onClose, onSaved, remove]);

  return (
    <EditorShell
      disabled={!valid}
      eyebrow={eyebrowText()}
      failure={flow.failure}
      host={host}
      loadFailed={filters.isError}
      loading={filters.isPending}
      onSave={save}
      pending={flow.pending}
      title={editTitle()}
    >
      <NameField error={!validGymName(name)} onChange={setName} value={name} />
      <div className="mt-5">
        <ChoiceForm
          label={placeLabel()}
          onChange={setPlace}
          options={placeOptions()}
          value={place}
        />
      </div>
      <div className="mt-5">
        <EquipmentForm
          error={equipmentIds.length === 0 ? equipmentEmpty() : undefined}
          onChange={setEquipmentIds}
          rows={filters.data?.equipment ?? []}
          value={equipmentIds}
        />
      </div>
      <div className="mt-6 flex flex-col gap-2">
        <Button
          data-testid="gym-switch"
          onClick={switchTo}
          size="lg"
          variant="secondary"
        >
          {switchLabel()}
        </Button>
        <Button
          data-testid="gym-delete"
          onClick={askDelete}
          size="lg"
          variant="ghost"
        >
          {deleteLabel()}
        </Button>
      </div>
      <AlertDialog
        confirmLabel={deleteConfirm()}
        description={deleteBody()}
        onConfirm={confirmDelete}
        onOpenChange={confirmOpenChange}
        open={confirming}
        pending={removal.isPending}
        title={deleteTitle({ name: gym.name })}
        tone="destructive"
      />
    </EditorShell>
  );
}
